import * as React from "react";
import { useState } from "react";

const MACROS = [
  { label: "{AgentName}", description: "Name of the assigned agent" },
  { label: "{VisitorName}", description: "Visitor's detected name" },
  { label: "{Chat}{EndChat}", description: "Wrap a link/button to open chat" },
  { label: "{Close}{EndClose}", description: "Wrap a link/button to close/dismiss the invitation" },
  { label: "{Message}", description: "Custom message text (manually-created invitations only)" }
];

interface Props {
  htmlContent: string;
  cssContent: string;
  onHtmlChange(html: string): void;
  onCssChange(css: string): void;
}

export default function HtmlSkinEditor({
  htmlContent,
  cssContent,
  onHtmlChange,
  onCssChange
}: Props) {
  const [tab, setTab] = useState<"html" | "css">("html");

  function insertMacro(macro: string) {
    onHtmlChange(htmlContent + macro);
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Security notice */}
      <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
        <strong>HTML is sanitized on save.</strong> Allowed: layout tags (div, span, p, h1–h6, a, img, button, table), class, href, src, alt attributes.
        Not allowed: inline style, id, script, iframe, event handlers, http:// links. Use the CSS tab for styling.
      </div>

      {/* Macro reference */}
      <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2">
        <div className="text-xs font-medium text-slate-700 mb-1.5">Available macros</div>
        <div className="flex flex-wrap gap-1.5">
          {MACROS.map((m) => (
            <button
              key={m.label}
              type="button"
              title={m.description}
              onClick={() => insertMacro(m.label)}
              className="text-xs font-mono bg-white border border-slate-300 rounded px-1.5 py-0.5 hover:bg-blue-50 hover:border-blue-400 text-slate-700"
            >
              {m.label}
            </button>
          ))}
        </div>
        <div className="text-xs text-slate-500 mt-1">Click to append to the HTML editor</div>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-0 border-b border-slate-200">
        {(["html", "css"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`px-4 py-1.5 text-xs font-medium uppercase tracking-wide border-b-2 -mb-px transition-colors ${
              tab === t
                ? "border-blue-500 text-blue-700"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.toUpperCase()}
          </button>
        ))}
      </div>

      {tab === "html" && (
        <textarea
          className="w-full h-56 rounded-md border border-slate-300 font-mono text-xs p-2 resize-y focus:outline-none focus:ring-1 focus:ring-blue-400"
          placeholder={'<div class="vi-card">\n  <p class="vi-msg">Hi {VisitorName}! Need help?</p>\n  <a class="vi-btn" href="#" data-velaro-action="chat">{Chat}Start chat{EndChat}</a>\n  <button class="vi-close">{Close}✕{EndClose}</button>\n</div>'}
          value={htmlContent}
          onChange={(e) => onHtmlChange(e.target.value)}
          spellCheck={false}
        />
      )}

      {tab === "css" && (
        <textarea
          className="w-full h-56 rounded-md border border-slate-300 font-mono text-xs p-2 resize-y focus:outline-none focus:ring-1 focus:ring-blue-400"
          placeholder={".velaro-invite {\n  background: #fff;\n  border-radius: 12px;\n  padding: 16px;\n  box-shadow: 0 4px 24px rgba(0,0,0,.12);\n}"}
          value={cssContent}
          onChange={(e) => onCssChange(e.target.value)}
          spellCheck={false}
        />
      )}
    </div>
  );
}
